import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { Option, showAllFilterOption } from '../../core/components/Fields/AutoCompleteField';
import { useExtractText, useLookupOptions, useMasterdataBrowser } from './useLookup';
import useForceSelection from './useForceSelection';
import { useAssistanceFieldContext } from '../pages/Assistance/AssistanceFieldContext';
import { useAssistanceViewContext } from '../pages/Assistance/AssistanceViewContext';
import useReselectEventListener, { ReselectEvent } from '../pages/Assistance/useReselectEventListener';
import { useAssistanceContext } from '../pages/Assistance/AssistanceContext';
import { useControllableState } from '../../core/utils/useControllableState.tsx';

export const useForceSelectionLookupProps = ({
    lookupType,
    lookupDefinitionId,
    lookupFieldName,
    partitionId,
    allowInvalidIds,
    value,
    onValueChange,
    onUpdatePayloadChange, // TODO: maybe directly in onSelectOption
    onFocus,
    onBlur,
    onCommit,
    isPartitionIdField = false,
    minSearchChars = 0,
    transformSearchTerm,
    lookupMatchingProps = null,
    resetInvalidOnBlur = false,
    initialSkip = true,
    skip: propsSkip,
    onSkipChange,
    fetchPolicy = 'no-cache',
    extractionSearchResults = null,
}: any) => {
    const { t } = useTranslation('assistance');
    const { handlers } = useAssistanceContext();

    const [inputValue, setInputValue] = useState('');

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    const notEnoughChars = inputValue.length < minSearchChars;
    const searchTerm = notEnoughChars ? '' : inputValue;

    const transformedSearchTerm = transformSearchTerm ? transformSearchTerm(searchTerm) : searchTerm;

    const [skip, setSkip] = useControllableState(initialSkip, propsSkip, onSkipChange);

    const handleFocus = (e: any) => {
        setSkip(false);
    };

    const handleBlur = (e: any) => {
        setSkip(true);
    };

    const { options, isLoading } = useLookupOptions({
        lookupDefinitionId,
        lookupFieldName,
        partitionId,
        searchTerm: transformedSearchTerm,
        extraSearchTerms: lookupMatchingProps?.extraSearchTerms,
        searchLookupOnEmptyValue: lookupMatchingProps?.searchLookupOnEmptyValue,
        skip,
        fetchPolicy,
        limit: lookupMatchingProps?.limit,
    });

    const forceSelectionOptions = useForceSelection({
        value,
        onValueChange,
        resetInvalidOnBlur,
        onFocus: onFocus,
        onBlur: onBlur,
        onCommit,
        onSelectOption: (option: Option) => {
            const updatePayload = option
                ? {
                      _lookup_id: option?.id,
                      _lookupEntity: option,
                  }
                : null;
            onUpdatePayloadChange?.(updatePayload);
            // returned so useForceSelection can attach it to the commit of this selection
            return updatePayload;
        },
        forceSelection: !allowInvalidIds,
        options,
    });

    const browserOptions = useMasterdataBrowser({
        lookupType,
        lookupDefinitionId,
        lookupFieldName,
        partitionId,
        searchTerm: transformedSearchTerm,
        options,
        onSelectOption: forceSelectionOptions.onSelectOption,
        onBlur: forceSelectionOptions.onBlur,
        onFocus: forceSelectionOptions.onFocus,
        isPartitionIdField: isPartitionIdField,
        lookupMatchingProps: lookupMatchingProps,
        extractionSearchResults: extractionSearchResults,
    });

    return {
        value: forceSelectionOptions.value,
        onValueChange: (value: string) => {
            forceSelectionOptions.onValueChange(value || '');
            setInputValue(value || '');
        },
        onFocus: (e: any) => {
            handleFocus(e);
            browserOptions.onFocus(e);
        },
        onBlur: (e: any) => {
            handleBlur(e);
            browserOptions.onBlur();
        },
        // the force-selection hook owns the commits; consumers commonly spread their full
        // fieldProps onto the base field, so explicitly keep onCommit away from it
        onCommit: undefined,

        onSelectOption: browserOptions.onSelectOption,
        onSelect: browserOptions.onSelect,

        options: browserOptions.options,
        filterOption: showAllFilterOption,

        isLoading,
        emptyMessage: notEnoughChars
            ? t('fields.autocompleteField.notEnoughChars')
            : t('fields.autocompleteField.noResults'),

        isMasterdataBrowserOpen: handlers?.masterdataBrowserModalVisible || handlers?.masterdataBrowserSidePanelVisible,
    };
};

export const useForceSelectionReselectEventListener = (inputRef: any, onValueChange: any) => {
    const { documentConfiguration } = useAssistanceContext();
    const { fullFieldName, itemIndex, itemType } = useAssistanceFieldContext();
    const { onFieldPending } = useAssistanceViewContext();

    const extractText = useExtractText(
        documentConfiguration.EXTRACT_TEXT,
        `${documentConfiguration?.documentTypeName}ExtractText`
    );

    const handleReselect = useCallback(
        (e: ReselectEvent) => {
            const extracted = extractText(e.detail.bbox, e.detail.pageIndex, e.detail.isEmailOcr);
            // loading until the text is in; registered first so it ends before the lookup's own loading starts
            onFieldPending({ fieldName: fullFieldName, itemIndex, itemType }, extracted);
            extracted.then((text) => {
                onValueChange(text);
                // reset focus to input
                inputRef.current?.focus();
                // ... and trigger keydown to start the search again
                inputRef.current?.dispatchEvent(new KeyboardEvent('keydown', { key: '', bubbles: true }));
            });
        },
        [extractText, onValueChange, onFieldPending, fullFieldName, itemIndex, itemType]
    );

    useReselectEventListener(inputRef, handleReselect);
};

export default useForceSelectionLookupProps;
